import { tzName } from "@date-fns/tz";
import { InteractiveDisplayState, requiresEnd } from "./interactive.model";
import { InteractiveListItem, InteractiveStatusFilter, InteractiveTypeParam } from "./interactives-list.model";
import { ConfirmModalData } from "../../components/confirm-modal/confirm-modal.component";

export type StatusPillValue = "ALL" | InteractiveStatusFilter;

export type TypeOptionValue = "ALL" | InteractiveTypeParam;

export const STATUS_PILLS: readonly { label: string; value: StatusPillValue }[] = [
    { label: "All", value: "ALL" },
    { label: "Live", value: "LIVE" },
    { label: "Scheduled", value: "SCHEDULED" },
    { label: "Paused", value: "PAUSED" },
    { label: "Draft", value: "DRAFT" },
    { label: "Closed", value: "CLOSED" },
    { label: "Archived", value: "ARCHIVED" }
];

export const TYPE_OPTIONS: readonly { label: string; value: TypeOptionValue }[] = [
    { label: "All Interactive Types", value: "ALL" },
    { label: "Voice/Text/Video", value: "FREEFORM" },
    { label: "Polls", value: "POLL" }
];

export type InteractiveMenuAction = "edit" | "duplicate" | "pause" | "resume" | "close" | "archive" | "delete";

/** Actions that mutate state through a confirm modal + endpoint, as opposed to plain navigation. */
export type StateChangeAction = Extract<InteractiveMenuAction, "pause" | "resume" | "close" | "archive" | "delete">;

/** A single row-menu entry; `link` is set for navigation items (Edit / Duplicate). */
export interface InteractiveMenuItem {
    action: InteractiveMenuAction;
    label: string;
    link?: string[];
}

export const MENU_ACTION_LABELS: Record<InteractiveMenuAction, string> = {
    edit: "Edit",
    pause: "Pause",
    resume: "Resume",
    close: "Close",
    delete: "Delete",
    archive: "Archive",
    duplicate: "Duplicate"
};

/** Menu items shown per display state, in the order they should appear. */
export const MENU_ACTIONS_BY_STATE: Record<InteractiveDisplayState, readonly InteractiveMenuAction[]> = {
    DRAFT: ["edit", "delete", "duplicate"],
    SCHEDULED: ["edit", "delete", "archive", "duplicate"],
    LIVE: ["edit", "pause", "close", "delete", "duplicate"],
    // PUBLISHED always resolves to LIVE / SCHEDULED / CLOSED via getInteractiveDisplayState,
    // so this key is never actually rendered. Kept only to satisfy the Record type.
    PUBLISHED: [],
    PAUSED: ["edit", "resume", "close", "delete", "archive", "duplicate"],
    CLOSED: ["delete", "duplicate"],
    ARCHIVED: ["delete", "duplicate"]
};

/** All user-facing copy for a state-change action: the confirm modal plus success/error toasts. */
export interface StateChangeCopy {
    confirm: ConfirmModalData;
    success: string;
    error: string;
}

export const STATE_CHANGE_COPY: Record<StateChangeAction, StateChangeCopy> = {
    pause: {
        confirm: {
            title: "Pause Interactive?",
            message: "Listeners won’t be able to submit while this interactive is paused.",
            confirmLabel: "Pause interactive"
        },
        success: "Interactive paused.",
        error: "We couldn’t pause this interactive. Try again."
    },
    resume: {
        confirm: {
            title: "Resume Interactive?",
            message: "This interactive will be live again, listeners will be able to submit again.",
            confirmLabel: "Resume interactive"
        },
        success: "Interactive is live again.",
        error: "We couldn’t resume this interactive. Try again."
    },
    close: {
        confirm: {
            title: "Close Interactive?",
            message:
                "This will remove it from all listener views. Single-select poll results remain visible for 24 hours.",
            confirmLabel: "Close interactive"
        },
        success: "Interactive closed.",
        error: "We couldn’t close this interactive. Try again."
    },
    archive: {
        confirm: {
            title: "Archive Interactive?",
            message:
                "This will cancel the interactive before it goes live and move it to Archived. Archived interactives stay in your records but won’t appear in active views.",
            confirmLabel: "Archive interactive"
        },
        success: "Interactive archived.",
        error: "We couldn’t archive this interactive. Try again."
    },
    delete: {
        confirm: {
            title: "Delete Interactive?",
            message: "This permanently deletes the interactive. This action can’t be undone.",
            confirmLabel: "Delete interactive",
            danger: true
        },
        success: "Interactive deleted.",
        error: "We couldn’t delete this interactive. Try again."
    }
};

/** Timezone the schedule copy is written in, matching what the row renders dates in. */
const SCHEDULE_TIMEZONE = "America/New_York";

/**
 * The confirm-modal copy for an action, with the interactive's schedule folded in where it matters.
 *
 * Resuming leaves an existing end date in place, so that end date is spelled out rather than
 * leaving the producer to assume the interactive will now run indefinitely. An interactive whose
 * time window carries no end is ongoing, and gets the plain copy.
 */
export function stateChangeConfirmData(action: StateChangeAction, item: InteractiveListItem): ConfirmModalData {
    const confirm = STATE_CHANGE_COPY[action].confirm;
    if (action !== "resume" || !requiresEnd(item.timeWindow) || item.endDate === null) return confirm;

    return {
        ...confirm,
        message: [
            "This interactive will go live again, and listeners will be able to submit. " +
                "This interactive will still end on ",
            { text: formatScheduleDate(new Date(item.endDate)), bold: true },
            " unless you update the schedule."
        ]
    };
}

/**
 * "Apr 25, 2026 at 12:00 PM ET" — the schedule wording for prose. Deliberately unlike TzDatePipe's
 * "Apr 25, 2026 12:00 PM EDT": a sentence reads better with "at", and the generic zone name spares
 * the reader working out whether daylight time applies on that date.
 */
function formatScheduleDate(date: Date): string {
    const datePart = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: SCHEDULE_TIMEZONE
    }).format(date);
    const timePart = new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: SCHEDULE_TIMEZONE
    }).format(date);
    return `${datePart} at ${timePart} ${tzName(SCHEDULE_TIMEZONE, date, "shortGeneric")}`;
}
